import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  VStack,
  HStack,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Select,
  Button,
  useColorModeValue
} from '@chakra-ui/react';
import {
  MdAnalytics,
  MdFeedback,
  MdPeople,
  MdSpeed,
  MdError,
  MdTrendingUp,
  MdRefresh,
  MdCloud,
  MdMonitor,
} from 'react-icons/md';
import { feedbackService, FeedbackType, FeedbackPriority, FeedbackData } from '../../services/feedback';
import { cloudMonitoring, SystemMetrics, UpptimeCheck, Alert } from '../../services/cloudMonitoring';

interface FeedbackMetrics {
  total: number;
  byType: Record<string, number>;
  bySentiment: Record<string, number>;
  byPriority: Record<string, number>;
  averageRating: number;
}

export const Monitoring = () => {
  const { t } = useTranslation(['common', 'dashboard']);
  const [feedbackStats, setFeedbackStats] = useState<FeedbackMetrics | null>(null);
  const [recentFeedback, setRecentFeedback] = useState<FeedbackData[]>([]);
  const [systemMetrics, setSystemMetrics] = useState<SystemMetrics | null>(null);
  const [uptimeChecks, setUptimeChecks] = useState<UpptimeCheck[]>([]);
  const [activeAlerts, setActiveAlerts] = useState<Alert[]>([]);
  const [timeRange, setTimeRange] = useState('7d');
  const [loading, setLoading] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    loadFeedbackData();
  }, [timeRange]);

  const loadFeedbackData = async () => {
    setLoading(true);
    try {
      const stats = await feedbackService.getFeedbackStats();
      setFeedbackStats(stats);

      // Get recent feedback (limit to 50)
      const recent = await feedbackService.getRecentFeedback(undefined, 50);
      setRecentFeedback(recent);

      // Load cloud monitoring data
      const metrics = await cloudMonitoring.getSystemMetrics();
      setSystemMetrics(metrics);

      const uptime = await cloudMonitoring.getUptimeChecks();
      setUptimeChecks(uptime);

      const alerts = await cloudMonitoring.getActiveAlerts();
      setActiveAlerts(alerts);
    } catch (error) {
      console.error('Error loading monitoring data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case FeedbackPriority.CRITICAL:
        return 'red';
      case FeedbackPriority.HIGH:
        return 'orange';
      case FeedbackPriority.MEDIUM:
        return 'yellow';
      case FeedbackPriority.LOW:
        return 'green';
      default:
        return 'gray';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case FeedbackType.BUG:
        return 'red';
      case FeedbackType.FEATURE_REQUEST:
        return 'blue';
      case FeedbackType.IMPROVEMENT:
        return 'purple';
      case FeedbackType.UX_ISSUE:
        return 'orange';
      case FeedbackType.PERFORMANCE:
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const formatDate = (timestamp: unknown) => {
    if (!timestamp) return '-';
    const date = (timestamp as { toDate?: () => Date }).toDate ? (timestamp as { toDate: () => Date }).toDate() : new Date(timestamp as string);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between">
          <Heading size="lg">
            <Icon as={MdAnalytics} mr={2} />
            {t('common.monitoring', 'Monitoring & Analytics')}
          </Heading>
          <HStack>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              w="150px"
            >
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
            </Select>
            <Button
              leftIcon={<MdRefresh />}
              onClick={loadFeedbackData}
              isLoading={loading}
              colorScheme="brand"
            >
              Refresh
            </Button>
          </HStack>
        </HStack>

        <Tabs colorScheme="brand">
          <TabList>
            <Tab>
              <Icon as={MdCloud} mr={2} />
              Cloud Infrastructure
            </Tab>
            <Tab>
              <Icon as={MdAnalytics} mr={2} />
              Analytics Overview
            </Tab>
            <Tab>
              <Icon as={MdFeedback} mr={2} />
              User Feedback
            </Tab>
            <Tab>
              <Icon as={MdError} mr={2} />
              Errors & Issues
            </Tab>
            <Tab>
              <Icon as={MdSpeed} mr={2} />
              Performance
            </Tab>
          </TabList>

          <TabPanels>
            {/* Cloud Infrastructure Tab */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                {/* System Metrics */}
                {systemMetrics && (
                  <>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                      <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                        <CardBody>
                          <Stat>
                            <StatLabel>CPU Usage</StatLabel>
                            <StatNumber>{systemMetrics.cpu.usage.toFixed(1)}%</StatNumber>
                            <StatHelpText>
                              <StatArrow type={systemMetrics.cpu.trend === 'up' ? 'increase' : 'decrease'} />
                              {systemMetrics.cpu.trend}
                            </StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>

                      <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                        <CardBody>
                          <Stat>
                            <StatLabel>Memory Usage</StatLabel>
                            <StatNumber>{systemMetrics.memory.usage.toFixed(1)} GB</StatNumber>
                            <StatHelpText>
                              of {systemMetrics.memory.total} GB total
                            </StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>

                      <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                        <CardBody>
                          <Stat>
                            <StatLabel>Requests/sec</StatLabel>
                            <StatNumber>{systemMetrics.network.requestsPerSecond}</StatNumber>
                            <StatHelpText>
                              Error rate: {systemMetrics.network.errorRate}%
                            </StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>

                      <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                        <CardBody>
                          <Stat>
                            <StatLabel>Avg Latency</StatLabel>
                            <StatNumber>{systemMetrics.network.avgLatency}ms</StatNumber>
                            <StatHelpText>Response time</StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>
                    </SimpleGrid>

                    {/* Uptime Checks */}
                    <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                      <CardHeader>
                        <Heading size="md">Service Uptime</Heading>
                      </CardHeader>
                      <CardBody>
                        <Table variant="simple">
                          <Thead>
                            <Tr>
                              <Th>Service</Th>
                              <Th>Status</Th>
                              <Th>Response Time</Th>
                              <Th>Uptime</Th>
                              <Th>Last Checked</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {uptimeChecks.map((check, index) => (
                              <Tr key={index}>
                                <Td fontWeight="medium">{check.name}</Td>
                                <Td>
                                  <Badge
                                    colorScheme={
                                      check.status === 'up' ? 'green' :
                                      check.status === 'degraded' ? 'yellow' : 'red'
                                    }
                                  >
                                    {check.status.toUpperCase()}
                                  </Badge>
                                </Td>
                                <Td>{check.responseTime}ms</Td>
                                <Td>{check.uptime.toFixed(2)}%</Td>
                                <Td fontSize="sm">{check.lastChecked.toLocaleTimeString()}</Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </CardBody>
                    </Card>

                    {/* Active Alerts */}
                    <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                      <CardHeader>
                        <Heading size="md">
                          Active Alerts ({activeAlerts.length})
                        </Heading>
                      </CardHeader>
                      <CardBody>
                        {activeAlerts.length > 0 ? (
                          <VStack spacing={3} align="stretch">
                            {activeAlerts.map((alert) => (
                              <Box
                                key={alert.id}
                                p={4}
                                borderRadius="md"
                                borderWidth="1px"
                                borderColor={
                                  alert.severity === 'critical' ? 'red.500' :
                                  alert.severity === 'warning' ? 'orange.500' : 'blue.500'
                                }
                              >
                                <HStack justify="space-between" mb={2}>
                                  <Text fontWeight="bold">{alert.name}</Text>
                                  <Badge
                                    colorScheme={
                                      alert.severity === 'critical' ? 'red' :
                                      alert.severity === 'warning' ? 'orange' : 'blue'
                                    }
                                  >
                                    {alert.severity}
                                  </Badge>
                                </HStack>
                                <Text fontSize="sm" color="gray.600">{alert.message}</Text>
                              </Box>
                            ))}
                          </VStack>
                        ) : (
                          <Text color="green.500" fontWeight="bold">
                            ✓ All systems operational - No active alerts
                          </Text>
                        )}
                      </CardBody>
                    </Card>

                    {/* Quick Links */}
                    <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                      <CardHeader>
                        <Heading size="md">Google Cloud Console</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing={3} align="stretch">
                          <Button
                            variant="outline"
                            justifyContent="flex-start"
                            leftIcon={<Icon as={MdMonitor} />}
                            as="a"
                            href={cloudMonitoring.getMonitoringDashboardUrl()}
                            target="_blank"
                          >
                            Monitoring Dashboards
                          </Button>
                          <Button
                            variant="outline"
                            justifyContent="flex-start"
                            leftIcon={<Icon as={MdError} />}
                            as="a"
                            href={cloudMonitoring.getLogsExplorerUrl()}
                            target="_blank"
                          >
                            Logs Explorer
                          </Button>
                          <Button
                            variant="outline"
                            justifyContent="flex-start"
                            leftIcon={<Icon as={MdSpeed} />}
                            as="a"
                            href={cloudMonitoring.getTraceExplorerUrl()}
                            target="_blank"
                          >
                            Trace Explorer
                          </Button>
                        </VStack>
                      </CardBody>
                    </Card>
                  </>
                )}
              </VStack>
            </TabPanel>

            {/* Analytics Overview Tab */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                {/* Key Metrics */}
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                  <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                    <CardBody>
                      <Stat>
                        <StatLabel>
                          <Icon as={MdPeople} mr={2} />
                          Total Users
                        </StatLabel>
                        <StatNumber>--</StatNumber>
                        <StatHelpText>
                          <StatArrow type="increase" />
                          -- from last week
                        </StatHelpText>
                      </Stat>
                    </CardBody>
                  </Card>

                  <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                    <CardBody>
                      <Stat>
                        <StatLabel>
                          <Icon as={MdTrendingUp} mr={2} />
                          Active Users
                        </StatLabel>
                        <StatNumber>--</StatNumber>
                        <StatHelpText>Last 7 days</StatHelpText>
                      </Stat>
                    </CardBody>
                  </Card>

                  <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                    <CardBody>
                      <Stat>
                        <StatLabel>Page Views</StatLabel>
                        <StatNumber>--</StatNumber>
                        <StatHelpText>
                          <StatArrow type="increase" />
                          -- from last week
                        </StatHelpText>
                      </Stat>
                    </CardBody>
                  </Card>

                  <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                    <CardBody>
                      <Stat>
                        <StatLabel>Avg. Session</StatLabel>
                        <StatNumber>-- min</StatNumber>
                        <StatHelpText>Duration per user</StatHelpText>
                      </Stat>
                    </CardBody>
                  </Card>
                </SimpleGrid>

                {/* Instructions */}
                <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                  <CardHeader>
                    <Heading size="md">Google Analytics Integration</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack align="start" spacing={3}>
                      <Text>
                        This dashboard will show real-time analytics data from Google Analytics 4.
                      </Text>
                      <Text fontWeight="bold">To view detailed analytics:</Text>
                      <VStack align="start" pl={4} spacing={2}>
                        <Text>
                          1. Visit{' '}
                          <Text as="span" color="brand.500" fontWeight="bold">
                            Google Analytics Dashboard
                          </Text>
                        </Text>
                        <Text>2. Select your property (un-dollar-rentflow-dev)</Text>
                        <Text>3. View Realtime, Engagement, and User reports</Text>
                      </VStack>
                      <Text fontSize="sm" color="gray.500" mt={2}>
                        Measurement ID: G-0N6B9GNZ42
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>
              </VStack>
            </TabPanel>

            {/* User Feedback Tab */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                {/* Feedback Stats */}
                {feedbackStats && (
                  <>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                      <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                        <CardBody>
                          <Stat>
                            <StatLabel>Total Feedback</StatLabel>
                            <StatNumber>{feedbackStats.total}</StatNumber>
                            <StatHelpText>All time</StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>

                      <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                        <CardBody>
                          <Stat>
                            <StatLabel>Average Rating</StatLabel>
                            <StatNumber>
                              {feedbackStats.averageRating > 0
                                ? feedbackStats.averageRating.toFixed(1)
                                : '-'}
                            </StatNumber>
                            <StatHelpText>Out of 5.0</StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>

                      <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                        <CardBody>
                          <VStack align="start" spacing={2}>
                            <Text fontSize="sm" fontWeight="bold">
                              By Type
                            </Text>
                            {Object.entries(feedbackStats.byType).map(([type, count]) => (
                              <HStack key={type} justify="space-between" w="full">
                                <Badge colorScheme={getTypeColor(type)}>{type}</Badge>
                                <Text fontWeight="bold">{count}</Text>
                              </HStack>
                            ))}
                          </VStack>
                        </CardBody>
                      </Card>

                      <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                        <CardBody>
                          <VStack align="start" spacing={2}>
                            <Text fontSize="sm" fontWeight="bold">
                              By Priority
                            </Text>
                            {feedbackStats.byPriority && Object.entries(feedbackStats.byPriority).map(([priority, count]) => (
                              <HStack key={priority} justify="space-between" w="full">
                                <Badge colorScheme={getPriorityColor(priority)}>{priority}</Badge>
                                <Text fontWeight="bold">{count}</Text>
                              </HStack>
                            ))}
                          </VStack>
                        </CardBody>
                      </Card>
                    </SimpleGrid>

                    {/* Recent Feedback Table */}
                    <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                      <CardHeader>
                        <Heading size="md">Recent Feedback</Heading>
                      </CardHeader>
                      <CardBody>
                        {recentFeedback.length > 0 ? (
                          <Box overflowX="auto">
                            <Table variant="simple">
                              <Thead>
                                <Tr>
                                  <Th>Date</Th>
                                  <Th>Type</Th>
                                  <Th>Priority</Th>
                                  <Th>Message</Th>
                                  <Th>Rating</Th>
                                  <Th>Page</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {recentFeedback.map((feedback, index) => (
                                  <Tr key={feedback.timestamp?.toString() || index}>
                                    <Td fontSize="sm">{formatDate(feedback.timestamp)}</Td>
                                    <Td>
                                      <Badge colorScheme={getTypeColor(feedback.type)}>
                                        {feedback.type}
                                      </Badge>
                                    </Td>
                                    <Td>
                                      <Badge colorScheme={getPriorityColor(feedback.priority || 'low')}>
                                        {feedback.priority || 'low'}
                                      </Badge>
                                    </Td>
                                    <Td maxW="300px" isTruncated>
                                      {feedback.message}
                                    </Td>
                                    <Td>{feedback.rating ? `${feedback.rating}/5` : '-'}</Td>
                                    <Td fontSize="sm">{feedback.page || '-'}</Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                        ) : (
                          <Text color="gray.500">No feedback submitted yet</Text>
                        )}
                      </CardBody>
                    </Card>
                  </>
                )}
              </VStack>
            </TabPanel>

            {/* Errors & Issues Tab */}
            <TabPanel>
              <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                <CardHeader>
                  <Heading size="md">Error Tracking</Heading>
                </CardHeader>
                <CardBody>
                  <VStack align="start" spacing={3}>
                    <Text>
                      Error tracking is configured through Google Analytics. All JavaScript errors and
                      failed API calls are automatically logged.
                    </Text>
                    <Text fontWeight="bold">To view error logs:</Text>
                    <VStack align="start" pl={4} spacing={2}>
                      <Text>1. Open Google Analytics</Text>
                      <Text>2. Navigate to Events → error</Text>
                      <Text>3. Filter by error_message parameter</Text>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            </TabPanel>

            {/* Performance Tab */}
            <TabPanel>
              <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                <CardHeader>
                  <Heading size="md">Performance Monitoring</Heading>
                </CardHeader>
                <CardBody>
                  <VStack align="start" spacing={3}>
                    <Text>
                      Performance metrics are tracked through Google Analytics, including page load
                      times, API response times, and user interactions.
                    </Text>
                    <Text fontWeight="bold">Tracked Performance Metrics:</Text>
                    <VStack align="start" pl={4} spacing={2}>
                      <Text>• Page Load Time (page_load_time event)</Text>
                      <Text>• API Call Duration (api_call event)</Text>
                      <Text>• Session Duration (session tracking)</Text>
                      <Text>• Time to Interactive</Text>
                    </VStack>
                    <Text fontWeight="bold" mt={4}>
                      Performance Targets:
                    </Text>
                    <VStack align="start" pl={4} spacing={2}>
                      <Text>• Page Load: {'<'} 3 seconds</Text>
                      <Text>• API Response: {'<'} 1 second</Text>
                      <Text>• Time to Interactive: {'<'} 5 seconds</Text>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Box>
  );
};

export default Monitoring;
